import React from "react";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import domtoimage from "dom-to-image";
import { saveAs } from "file-saver";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";

import "typeface-open-sans";

import RoomCardHeader from "./RoomCard/RoomCardHeader";
import RoomCardAdmin from "./RoomCard/RoomCardAdmin";
import { Autocomplete } from "@material-ui/lab";

//
// RoomCardGraph
//

class UnstyledRoomCardGraph extends React.Component {
  constructor() {
    super();
    this.downloadRef = React.createRef();
    this.downloadIconRef = React.createRef();
  }

  static styles() {
    return {
      Container: {
        width: "100%",
        height: "85%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "1em"
      },

      RechartsContainer: {
        backgroundColor: "#F8F9FC"
      },

      XAxis: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
      },

      YAxis: {
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        color: "#737F8C"
      }
    };
  }

  downloadGraphAsSvg() {
    const locationname = this.props.r.locations.length
      ? this.props.r.locations[0].human_name
      : "";
    const roomname = this.props.r.human_name;

    domtoimage
      .toSvg(this.downloadRef.current, {
        filter: node => !node.isEqualNode(this.downloadIconRef.current)
      })
      .then(dataUrl => {
        saveAs(
          dataUrl,
          `${locationname} ${roomname}.svg`.replace(/[^a-z0-9.]/gi, "_")
        );
      });
  }

  downloadGraphAsPng() {
    const locationname = this.props.r.locations.length
      ? this.props.r.locations[0].human_name
      : "";
    const roomname = this.props.r.human_name;

    domtoimage
      .toPng(this.downloadRef.current, {
        filter: node => !node.isEqualNode(this.downloadIconRef.current)
      })
      .then(dataUrl => {
        saveAs(
          dataUrl,
          `${locationname} ${roomname}.png`.replace(/[^a-z0-9.]/gi, "_")
        );
      });
  }

  render() {
    const classes = this.props.classes;

    const data = this.props.data;

    if (this.props.loading) {
      return (
        <Box className={classes.Container}>
          <CircularProgress className={classes.Progress} />
        </Box>
      );
    }
    let graph;
    if (data.length < 2) {
      graph = <Typography>Insufficient data.</Typography>;
    } else {
      graph = (
        <>
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{ height: "100%", width: "100%", flexWrap: "nowrap" }}
            ref={this.downloadRef}
          >
            <Grid
              item
              xs
              style={{ flexBasis: "1.75rem", flexGrow: "0", flexShrink: "0" }}
            >
              <Box>
                <Box className={classes.YAxis}>
                  <Typography>{this.props.label}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs
              style={{
                height: "100%",
                maxWidth: "calc(100% - 1.75rem)",
                flexBasis: "0",
                flexGrow: "1",
                flexShrink: "1"
              }}
            >
              <Box style={{ width: "100%", height: "100%" }}>
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  style={{ height: "100%", width: "100%" }}
                  wrap="nowrap"
                >
                  <Grid item style={{ width: "100%", height: "100%", flex: 1 }}>
                    <ResponsiveContainer
                      className={classes.RechartsContainer}
                      width={"100%"}
                    >
                      <LineChart data={data}>
                        <CartesianGrid
                          vertical={false}
                          horizontalPoints={[
                            25,
                            50,
                            75,
                            100,
                            125,
                            150,
                            175,
                            200,
                            225,
                            250
                          ]}
                        />
                        <XAxis
                          hide={true}
                          type={"number"}
                          dataKey={measurement => measurement.get("timestamp")}
                          domain={["dataMin", "dataMax"]}
                        />
                        <YAxis
                          hide={true}
                          type={"number"}
                          domain={[-25, 75]}
                          tickCount={10}
                        />
                        <Line
                          type={"monotone"}
                          dataKey={
                            this.props.label === "Relative Ethylene"
                              ? measurement => measurement.get("ppm")
                              : measurement => measurement.get("value")
                          }
                          isAnimationActive={false}
                          dot={false}
                          stroke={"#AFC322"}
                          strokeWidth={4}
                        />
                        {(this.props.user || {}).is_staff ? (
                          <Tooltip
                            labelFormatter={value =>
                              moment(value).format("LLLL")
                            }
                          />
                        ) : (
                          <Tooltip
                            labelFormatter={value =>
                              moment(value).format("LLLL")
                            }
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item style={{ width: "100%" }}>
                    <Box className={classes.XAxis}>
                      <Typography>
                        {moment(data[0].get("timestamp")).format("LL")}
                      </Typography>
                      <Typography>
                        {moment(data[data.length - 1].get("timestamp")).format(
                          "LL"
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          {(this.props.user || {}).is_staff ? (
            <>
              <Grid
                container
                direction="row"
                justify="space-evenly"
                alignItems="center"
              >
                <Grid item>
                  <Typography>
                    Predicted open:{" "}
                    {this.props.r.predicted_ready_timestamp
                      ? moment(this.props.r.predicted_ready_timestamp).format(
                          "LLL"
                        )
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    color="secondary"
                    size="small"
                    onClick={() => this.downloadGraphAsPng()}
                  >
                    Download .png
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            <> </>
          )}
        </>
      );
    }

    return <Box className={classes.Container}>{graph}</Box>;
  }
}

const RoomCardGraph = withStyles(UnstyledRoomCardGraph.styles)(
  observer(UnstyledRoomCardGraph)
);

// RoomCardGraph

const styles = theme => ({
  RoomCard: {
    width: "100%",
    minWidth: "40rem"
  },

  VarietyNotesBox: {
    padding: "1rem",
    overflowY: "auto"
  },

  VarietyBox: {
    width: "100%",
    marginBottom: "1rem"
  },

  Variety: {
    fontSize: "1rem",
    lineHeight: "1.25rem",
    fontWeight: "500",
    color: "#0A0E34"
  },

  GraphContainer: {
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    height: "100%"
  },

  LargeRoomListCard: {
    width: theme.spacing(62),
    height: theme.spacing(28)
  },
  RoomListCard: {
    width: theme.spacing(30),
    height: theme.spacing(28)
  },
  RoomListCardHeader: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  },
  SensorSelect: {
    paddingLeft: "0.5em",
    paddingRight: "0.5em",
    paddingTop: "0.5em"
  },
  ViewSwitcher: {
    display: "flex",
    flexDirection: "row"
  },
  ViewLabel: {
    cursor: "pointer",
    color: "rgba(0, 0, 0, 0.5)"
  },
  ActiveViewLabel: {
    fontWeight: 500,
    color: "rgba(0, 0, 0, 1.0)",
    borderBottom: ".1rem solid black"
  },
  RoomListCardBody: {
    padding: theme.spacing(1.5),
    paddingTop: 0,
    paddingBottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "space-between",
    justifyContent: "space-between"
  },

  SmallStatLabel: {
    color: "rgba(0, 0, 0, 0.5)",
    spacing: "2px",
    lineHeight: 1.1
  },
  SmallStatValue: {
    lineHeight: 1.1,
    marginBottom: theme.spacing(1.5)
  },

  RoomListCardStatusStable: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#c2e791"
  },
  RoomListCardStatusMaturing: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#f7cd84"
  },
  RoomListCardStatusMature: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#e6a2aa"
  },
  RoomListCardStatusNone: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#DCDCDC"
  },
  RoomListCardStatusOpen: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#444444"
  },

  progressContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
});

class UnstyledRoomCard extends React.Component {
  constructor(props) {
    super(props);
    this.optionsMenuAnchor = React.createRef();

    this.state = {
      optionsMenuOpen: false,
      deleteConfirmDialogOpen: false,
      deleteConfirmDialogTarget: undefined,
      nowViewActive: true,
      graphViewLoading: false,
      adminOpen: false,
      selectedSensor: undefined
    };
  }

  componentDidMount() {
    this.setNowViewActive(false);
  }

  setOptionsMenuOpen(open) {
    this.setState({ optionsMenuOpen: open });
  }

  setDeleteConfirmDialogOpen(open) {
    this.setState({ deleteConfirmDialogOpen: open });
  }

  setNowViewActive(active) {
    if (this.state.nowViewActive && !active && !this.state.graphViewLoading) {
      this.setState({ graphViewLoading: true });

      const room = this.props.rootStore.roomStore.rooms.get(this.props.room.id);
      if ((this.props.user || {}).is_staff) {
        room.pullSensors();
      }
      room.pullReadings().then(() => {
        this.setState({ graphViewLoading: false });
      });
    }
    this.setState({ nowViewActive: active });
  }

  setSensor(sensor) {
    if (!sensor) {
      this.setSensorEthylene();
    } else if (!this.state.nowViewActive && !this.state.graphViewLoading) {
      this.setState({ graphViewLoading: true });

      sensor.pullMeasurements().then(() => {
        this.setState({
          selectedSensor: sensor,
          graphViewLoading: false
        });
      });
    }
  }

  getSensorData(sensor) {
    const measurements = sensor.measurements;
    const sensorData = (measurements.toJSON() || []).map(
      m =>
        new Map(
          Object.entries({
            timestamp: Number(moment.unix(m.t).valueOf()),
            value: Number(m.v.toFixed(2))
          })
        )
    );
    return sensorData;
  }

  getEthyleneData() {
    const room = this.props.room;
    let data = (room.orderedReadings() || []).map(
      m =>
        new Map(
          Object.entries({
            id: m.id,
            timestamp: Number(moment(m.timestamp).valueOf()),
            ppm: Number(m.payload.ppm.toFixed(1))
          })
        )
    );
    return data;
  }

  setSensorEthylene() {
    this.setState({ selectedSensor: undefined });
  }

  handleClickAway() {
    this.setOptionsMenuOpen(false);
  }

  handleEdit() {
    this.props.history.push(`/rooms/${this.props.room.id}`);
  }

  handleDelete() {
    this.setOptionsMenuOpen(false);
    this.setDeleteConfirmDialogOpen(true);
  }

  setAdminOpen(open) {
    this.setState({ adminOpen: open });
  }

  renderDeleteConfirmDialog() {
    return (
      <Dialog
        open={this.state.deleteConfirmDialogOpen}
        onClose={() => this.setDeleteConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <b>{this.props.room.human_name}</b>?
          </DialogContentText>
          <DialogActions>
            <Button
              color="primary"
              onClick={() => this.setDeleteConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => {
                this.setDeleteConfirmDialogOpen(false);
                this.props.rootStore.roomStore.delete(this.props.room.id);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
  }

  render() {
    const classes = this.props.classes;
    const room = this.props.room;
    const human_name = room.human_name;
    const sensors = this.props.room.sensors;

    const varieties = room.varieties || [];
    const variety = varieties.length
      ? varieties
          .map(v => v.human_name || "")
          .filter(n => n !== "")
          .sort()
          .join(", ")
      : "";

    const customer_notes = room.customer_notes;
    const yAxisLabels = {
      "(temperature)": "Temperature  °C",
      "(humidity)": "Humidity  %",
      "(ethanol)": "Ethanol  ppm",
      "(o2)": "O2  %",
      "(stm32_temp)": "MCU Temperature  °C",
      "(batt)": "Battery Voltage",
      Ethylene: "Relative Ethylene"
    };
    const data = this.state.selectedSensor
      ? this.getSensorData(this.state.selectedSensor)
      : this.getEthyleneData();
    const label = this.state.selectedSensor
      ? yAxisLabels[this.state.selectedSensor.human_name.split(" ")[1]]
      : yAxisLabels["Ethylene"];

    let notesSection;
    if (customer_notes !== "") {
      notesSection = (
        <Typography style={{ whiteSpace: "pre-line" }}>
          {customer_notes}
        </Typography>
      );
    } else {
      notesSection = <Typography>No notes to display.</Typography>;
    }

    return (
      <>
        <Card className={classes.RoomCard}>
          <Grid container style={{ height: "25vw" }}>
            <Grid
              item
              xs={4}
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <RoomCardHeader
                priority={this.props.order}
                name={human_name}
                status={room.status}
              />
              <Divider />
              <Box className={classes.VarietyNotesBox}>
                <Box className={classes.VarietyBox}>
                  <Typography className={classes.Variety}>{variety}</Typography>
                </Box>
                <Box>{notesSection}</Box>
              </Box>
            </Grid>
            <Grid item xs={8} className={classes.GraphContainer}>
              {sensors.length > 1 && (this.props.user || {}).is_staff ? (
                <>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justify="space-evenly"
                  >
                    <Autocomplete
                      className={classes.SensorSelect}
                      id="sensor-data-selector"
                      disableClearable={false}
                      disabled={this.state.graphViewLoading}
                      size="small"
                      options={sensors}
                      clearText="Reset to ethylene"
                      groupBy={option => option.human_name.split(" ")[0]}
                      onChange={(event, newSensor) => {
                        this.setSensor(newSensor);
                      }}
                      getOptionLabel={option =>
                        option.human_name.replace(/[()]/g, "")
                      }
                      openText="List available sensors"
                      style={{ width: 240 }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Sensor type"
                          variant="outlined"
                          placeholder="humidity"
                        />
                      )}
                    />
                  </Grid>
                </>
              ) : (
                <></>
              )}
              <RoomCardGraph
                loading={this.state.graphViewLoading}
                data={data}
                user={this.props.user}
                r={room}
                label={label}
              />
            </Grid>
          </Grid>
          {(this.props.user || {}).is_staff ? (
            <>
              <Box className={classes.AdminDropdown}>
                {this.state.adminOpen ? (
                  <Grid
                    container
                    spacing={0}
                    direction="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    wrap="nowrap"
                    style={{
                      height: "100%",
                      width: "100%",
                      backgroundColor: "#D3D3D3"
                    }}
                  >
                    {this.state.adminOpen ? (
                      <Grid
                        item
                        xs={12}
                        onClick={() => this.setAdminOpen(false)}
                      >
                        <ArrowDropUpIcon fontSize="large" />
                      </Grid>
                    ) : (
                      <></>
                    )}
                    <Grid
                      item
                      xs={12}
                      style={{
                        width: "100%"
                      }}
                    >
                      <RoomCardAdmin room={room} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid
                    container
                    spacing={0}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: "100%",
                      width: "100%",
                      backgroundColor: "#D3D3D3"
                    }}
                  >
                    <Grid item xs={12} onClick={() => this.setAdminOpen(true)}>
                      <ArrowDropDownIcon fontSize="large" />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </>
          ) : (
            <></>
          )}
        </Card>
      </>
    );
  }
}

export default withRouter(
  withStyles(styles)(
    inject(stores => ({
      rootStore: stores.rootStore,
      user: stores.rootStore.authStore.user
    }))(observer(UnstyledRoomCard))
  )
);
